import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories, bannerComics } from '@/mocks/comics';
import type { ComicItem } from '@/mocks/types';

const siteUrl = 'https://tianbaolaowu.cn';

function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫漫画网',
    url: siteUrl,
    description: '韩漫漫画网为您推荐最新热门韩漫和最火排行榜，大全内容免费下拉式阅读，在线观看高清，每日更新丰富您的选择。',
  };
}

function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫漫画网',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

function getWebPageSchema(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#organization` },
  };
}

export default function Home() {
  const [activeRankTab, setActiveRankTab] = useState<'hot' | 'new' | 'rating'>('hot');
  const [currentBanner, setCurrentBanner] = useState(0);

  const hotComics = comics.filter((c) => c.isHot);
  const newComics = comics.filter((c) => c.isNew);
  const topRated = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const latestUpdates = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);

  const rankData: Record<string, ComicItem[]> = {
    hot: hotComics.slice(0, 8),
    new: newComics.slice(0, 8),
    rating: topRated,
  };

  const rankTabs: { key: 'hot' | 'new' | 'rating'; label: string }[] = [
    { key: 'hot', label: '热门榜' },
    { key: 'new', label: '新作榜' },
    { key: 'rating', label: '评分榜' },
  ];

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % bannerComics.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const seoData = {
    title: '韩漫漫画网_最新热门韩国漫画推荐_最火韩国漫画排行榜',
    description: '韩漫漫画网为您推荐最新热门韩漫和最火排行榜，大全内容免费下拉式阅读，在线观看高清，每日更新丰富您的选择。覆盖恋爱、奇幻、动作、悬疑等全部分类。',
    keywords: '韩漫漫画网,热门推荐,最火排行榜,漫画大全,免费下拉式,在线阅读,韩国漫画',
    canonical: siteUrl,
    ogType: 'website',
    schema: [getOrganizationSchema(), getWebSiteSchema(), getWebPageSchema('韩漫漫画网 - 首页', '韩漫漫画网为您推荐最新热门韩漫和最火排行榜，大全内容免费下拉式阅读，在线观看高清，每日更新丰富您的选择。', siteUrl)],
  };

  return (
    <>
      <SEOHead data={seoData} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Banner */}
          <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden">
            {bannerComics.map((comic, index) => (
              <div
                key={comic.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto w-full px-4 md:px-6">
                    <div className="max-w-xl">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-4 whitespace-nowrap">
                        热门推荐
                      </span>
                      <h2 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                        {comic.title}
                      </h2>
                      <p className="text-background-200 text-sm md:text-base leading-relaxed mb-5 line-clamp-2">
                        {comic.description}
                      </p>
                      <div className="flex items-center gap-4 text-background-300 text-sm mb-6">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-user-line"></i>
                          {comic.author}
                        </span>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-star-fill text-accent-400"></i>
                          {comic.rating}
                        </span>
                        <span className="whitespace-nowrap">{comic.chapters}话</span>
                      </div>
                      <Link
                        to={`/article/${comic.id}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        立即阅读
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {bannerComics.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBanner(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                    index === currentBanner ? 'bg-background-50 w-6' : 'bg-background-50/40 hover:bg-background-50/70'
                  }`}
                />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  分类浏览
                </h2>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer group"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 group-hover:scale-110 transition-transform">
                      <i className={`${cat.icon} text-lg`}></i>
                    </span>
                    <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Rankings Section */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  排行榜
                </h2>
              </div>
              <div className="flex items-center gap-1 bg-background-200/70 rounded-full p-1 w-fit mb-6">
                {rankTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveRankTab(tab.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeRankTab === tab.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {rankData[activeRankTab].map((comic, index) => (
                  <div key={comic.id} className="relative">
                    {index < 3 && (
                      <div className={`absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold text-background-50 ${
                        index === 0 ? 'bg-accent-500' : index === 1 ? 'bg-secondary-500' : 'bg-primary-500'
                      }`}>
                        {index + 1}
                      </div>
                    )}
                    <ComicCard comic={comic} />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
                <Link to="/category/love" className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap">
                  查看更多
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {latestUpdates.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Hot Tags */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                热门标签
              </h2>
              <div className="flex flex-wrap gap-2">
                {[...new Set(comics.flatMap((c) => c.tags))].map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/love`}
                    className="px-4 py-2 rounded-full text-sm bg-background-50 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}